/**
 * referralMixin.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Mixin Vue réutilisable pour tous les formulaires d'inscription PettyCash.
 *
 * Compatibilité DOUBLE :
 *   • Nouveau système  → URL param  ?ref=cGNfcmVmOjAyMFBDMQ==   (base64 chiffré)
 *   • Ancien système   → URL param  ?referral=020PC1              (texte brut)
 *   • Fallback         → champ pré-rempli vide, saisie manuelle possible
 *
 * États exposés au composant parent :
 *   referralCode          → serial_no décodé (ex: "020PC1")
 *   referralState         → 'idle' | 'loading' | 'valid' | 'invalid' | 'manual'
 *   referralOwnerName     → nom du parrain si l'API le renvoie
 *   referralLocked        → true si le code vient de l'URL (ne peut pas être modifié)
 *
 * Usage dans un composant :
 *   import referralMixin from '@/mixins/referralMixin'
 *   export default { mixins: [referralMixin], ... }
 * ─────────────────────────────────────────────────────────────────────────────
 */

import axios from 'axios'

export default {
  data () {
    return {
      referralCode: '',
      referralState: 'idle',    // idle | loading | valid | invalid | manual
      referralOwnerName: '',
      referralLocked: false,
      _referralDebounceTimer: null,
    }
  },


  mounted () {
    this._initReferralFromURL()
  },

  watch: {
    // Surveille les changements de route pour réinitialiser le code referral
    $route () {
      this._initReferralFromURL()
    }
  },

  methods: {
    // ─────────────────────────────────────────────────────────────────────────
    // 1. DÉCODAGE — lit l'URL et extrait le serial_no
    // ─────────────────────────────────────────────────────────────────────────
    _initReferralFromURL () {
      const params = new URLSearchParams(window.location.search)

      // ① Nouveau paramètre ?ref= (base64 chiffré)
      const encryptedRef = params.get('ref')
      if (encryptedRef && encryptedRef.trim()) {
        const decoded = this._decodeReferralCode(encryptedRef.trim())
        if (decoded) {
          this.referralCode = decoded
          this.referralLocked = true
          console.log('[ReferralMixin] Code chargé depuis URL ?ref=', { decoded, locked: true })
          this._verifyReferralCode(decoded)
          return
        }
      }

      // ② Ancien paramètre ?referral= (texte brut — rétrocompatibilité)
      const plainRef = params.get('referral') || params.get('affiliate_referral')
      if (plainRef && plainRef.trim()) {
        this.referralCode = plainRef.trim()
        this.referralLocked = true
        console.log('[ReferralMixin] Code chargé depuis URL ?referral=', { code: plainRef.trim(), locked: true })
        this._verifyReferralCode(plainRef.trim())
        return
      }

      // ③ Aucun code dans l'URL → saisie manuelle autorisée
      this.referralState = 'idle'
      this.referralLocked = false
      console.log('[ReferralMixin] Aucun code dans l\'URL → mode saisie manuelle')
    },

    // ─────────────────────────────────────────────────────────────────────────
    // 2. DÉCODAGE BASE64 URL-safe → serial_no
    //    Format attendu après décodage : "pc_ref:{serial_no}"
    // ─────────────────────────────────────────────────────────────────────────
    _decodeReferralCode (encoded) {
      try {
        // Revert URL-safe base64 → standard base64
        const standard = encoded
          .replace(/-/g, '+')
          .replace(/_/g, '/')

        // Padding si nécessaire
        const padded = standard + '='.repeat((4 - standard.length % 4) % 4)

        const decoded = atob(padded)

        // Vérifie le préfixe de sécurité
        if (decoded.startsWith('pc_ref:')) {
          return decoded.slice('pc_ref:'.length)
        }

        // Ancien format sans préfixe → on l'accepte quand même
        if (decoded && decoded.length > 2) {
          return decoded
        }

        return null
      } catch (err) {
        console.warn('[Referral] Échec décodage base64:', err.message)
        return null
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // 3. VÉRIFICATION API — appelle le backend pour confirmer l'existence
    // ─────────────────────────────────────────────────────────────────────────
    async _verifyReferralCode (serialNo) {
      if (!serialNo || !serialNo.trim()) {
        this.referralState = 'idle'
        return
      }

      this.referralState = 'loading'
      this.referralOwnerName = ''

      try {
        const response = await axios.get('/api/v1/home/verify-referral/', {
          params: { serial_no: serialNo.trim() }
        })

        if (response.data && response.data.valid) {
          this.referralState = 'valid'
          this.referralOwnerName = response.data.username || response.data.name || ''
        } else {
          this.referralState = 'invalid'
          // Si le code vient de l'URL mais est invalide → on déverrouille pour correction
          if (this.referralLocked) {
            this.referralLocked = false
          }
        }
      } catch (err) {
        console.error('[Referral] Erreur vérification:', err)
        // En cas d'erreur réseau → on laisse passer sans bloquer l'inscription
        this.referralState = 'invalid'
        if (this.referralLocked) {
          this.referralLocked = false
        }
      }
    },

    // ─────────────────────────────────────────────────────────────────────────
    // 4. SAISIE MANUELLE — déclenché par @input sur le champ texte
    //    Debounce 800ms pour éviter trop d'appels API
    // ─────────────────────────────────────────────────────────────────────────
    onReferralInput (value) {
      this.referralCode = value
      this.referralState = 'manual'
      this.referralOwnerName = ''

      clearTimeout(this._referralDebounceTimer)

      if (!value || value.trim().length < 3) {
        this.referralState = 'idle'
        return
      }

      this._referralDebounceTimer = setTimeout(() => {
        this._verifyReferralCode(value.trim())
      }, 800)
    },

    // ─────────────────────────────────────────────────────────────────────────
    // 5. HELPER — renvoie le serial_no propre à envoyer dans le body du form
    //    Envoie TOUS les codes sauf "020PC1" (défaut) qui représente pas de parrainage
    // ─────────────────────────────────────────────────────────────────────────
    getReferralPayload () {
      // Si aucun code saisi
      if (!this.referralCode || !this.referralCode.trim()) {
        console.log('[ReferralMixin] getReferralPayload: referralCode vide →', { referralCode: this.referralCode })
        return ''
      }

      const code = this.referralCode.trim()

      // 🎯 IGNORER UNIQUEMENT le code par défaut 020PC1
      if (code === '020PC1') {
        console.log('[ReferralMixin] getReferralPayload: code filtré (020PC1) → vide')
        return ''
      }

      // ✅ Envoyer TOUS les autres codes (valides, manuels, etc.)
      console.log('[ReferralMixin] getReferralPayload: renvoi du code →', { code })
      return code
    },
  }
}
